import * as React from "react";
import type { Omittable } from "../lib/props";
import { PORTALED_OVERLAY_SELECTOR } from "./use-outside-click";

export type UseEscapeDismissOptions = Omittable<{
  /** The overlay panel's root element. */
  ref: React.RefObject<{
    closest?: (selector: string) => Element | null;
  } | null>;
  /** When false, no listener is attached. */
  active: boolean;
  /** Called when Escape targets the panel. Absent = no listener. */
  onDismiss: (() => void) | undefined;
}>;

/**
 * Escape-to-dismiss for a NON-portalled overlay panel (a drawer overlaying its
 * host container). Base UI's own dismissal covers portalled layers; this hook
 * exists for the panel that has no popup of its own but must still win Escape
 * over the dialog hosting it.
 *
 * The listener runs in the CAPTURE phase on `document`, ahead of Base UI's
 * bubble-phase escape handling, and consumes the event — so the first Escape
 * closes the panel and only the next one reaches the host dialog. It stands
 * down whenever a portalled popup above the host layer owns focus (a select,
 * menu or alert dialog opened from the panel handles its own Escape), keyed by
 * where `document.activeElement` sits: the popup layers move focus into
 * themselves, the panel does not portal at all.
 */
export function useEscapeDismiss({
  ref,
  active,
  onDismiss,
}: UseEscapeDismissOptions): void {
  React.useEffect(() => {
    if (!active || !onDismiss) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const root = ref.current;
      if (!root?.closest) return;
      // The layer hosting the panel: the dialog it overlays, or null on a
      // plain page. Escape is the panel's only while focus is in that layer —
      // focus inside a DIFFERENT overlay means a popup sits above the panel.
      const host = root.closest(PORTALED_OVERLAY_SELECTOR);
      const activeElement = document.activeElement;
      const activeLayer =
        activeElement && activeElement !== document.body
          ? activeElement.closest(PORTALED_OVERLAY_SELECTOR)
          : // Focus parked on <body> means no popup owns it (popups move
            // focus into themselves), so the panel still takes the key.
            host;
      if (activeLayer !== host) return;
      event.preventDefault();
      event.stopPropagation();
      onDismiss();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [ref, active, onDismiss]);
}
